import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { GameSetup } from 'pkg/api/models/game';
import type { GameEvent, GameEventItemMeta } from 'pkg/api/models/game_event';
import type { GameReportVisibility } from 'pkg/api/models/game_report';
import type { SportType } from 'pkg/api/models/sport';

import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

export interface GameTeamPayload {
	groupId?: number;
	opponentId?: number;
}

export interface GameCreatePayload extends GameSetup {
	location: string;
	sportType: SportType;
	competitionID: number;
	homeGameTeam: GameTeamPayload;
	awayGameTeam: GameTeamPayload;
	startsAt: number;
}

export interface GameUpdatePayload extends GameSetup {
	location: string;
	competitionID: number;
	homeGameTeam: GameTeamPayload;
	awayGameTeam: GameTeamPayload;
	startsAt: number;
}

export interface Scorer {
	name: string;
	time: string;
}

export interface EventPeriodScore {
	id: number;
	value: number;
	label: string;
	period: number;
	tiebreakValue?: number;
	// The entry's index into the sport's extra periods, shown behind
	// checkboxes
	extraPeriod?: number;
}

// One of the sport's periods beyond regulation (e.g. overtime) — active
// drives the entry checkbox's initial state
export interface ExtraPeriod {
	label: string;
	active: boolean;
}

export interface EventBasePeriodScore {
	type: string;
	id: string;
	value: number;
	events: EventPeriodScore[];
}

export interface TeamResponse {
	id: number;
	score: number;
	name: string;
	value: number;
	events?: GameEvent[];
	profileImageUrl?: string;
	periodScores?: number[];
	periodScore?: EventBasePeriodScore;
	scorers?: Scorer[];
	groupId?: number;
	players?: { id: number; name: string; profileImageUrl?: string }[];
}

export interface GameReportPayload {
	title?: string;
	content: string;
	visibility: GameReportVisibility;
	attachmentIds?: number[];
}

export interface GameOverviewField {
	id: string;
	icon: MaterialSymbolVariant;
	label: string;
}

export interface GameOverview extends AllowedActionable {
	id: number;
	homeTeam: TeamResponse;
	awayTeam: TeamResponse;
	fields?: GameOverviewField[];
	sportType: string;
}

interface TimelineResponseUser {
	name: string;
	profileImageUrl: string;
	icon: MaterialSymbolVariant;
	label?: string;

	customIcon?: MaterialSymbolVariant;
}

interface TimelineResponse {
	time: string;
	icon: MaterialSymbolVariant;
	customIcon?: MaterialSymbolVariant;
	title: string;
	variant: 'normal' | 'muted' | 'highlight';
	subTitle?: string;
	period: number;
	periodOffset: number;

	users?: TimelineResponseUser[];
}

interface GameDescriptionFields {
	id?: string;
	icon: MaterialSymbolVariant;
	label: string;
}

export enum TimelineType {
	Normal = 'normal',
	TabView = 'tabView',
}

export enum PeriodScoreDisplayType {
	PeriodScore = 'periodScore',
	BoxScore = 'boxScore',
	SetScore = 'setScore',
	RacketTiebreakScore = 'racketTiebreakScore',
}

// A ready-to-render per-period value table served by the sport's event
// model, e.g. goals or shots per period
export interface PeriodBreakdown {
	label: string;
	// Per-period column headers, e.g. "1", "2", "3", "OT"
	columnLabels?: string[];
	home: number[];
	away: number[];
	homeTotal: number;
	awayTotal: number;
}

export interface GameEventResponse extends AllowedActionable {
	id: number;
	periods?: number;
	homeTeam: TeamResponse;
	awayTeam: TeamResponse;
	timeline: TimelineResponse[];
	timelineType?: TimelineType;
	periodTabLabels?: string[];
	// The sport's translated extra period names beyond regulation (overtime,
	// a shootout), in entry order
	extraPeriods?: ExtraPeriod[];
	periodScoreDisplayType?: PeriodScoreDisplayType;
	periodBreakdowns?: PeriodBreakdown[];
	sportType: string;
	showUserInputState: boolean;
	fields?: GameDescriptionFields[];
	groupId: number;
	// True when the game is a tournament team copy — its scores mirror the
	// master game and cannot be edited here.
	isTournamentTeamCopy?: boolean;
}

export interface GameEventsUpdatePayload {
	// Omitted when the score inputs are empty — the backend treats a missing
	// score as "no change".
	homeScore?: number;
	awayScore?: number;
	homeTeam: Partial<GameEventItemMeta | EventBasePeriodScore>[];
	awayTeam: Partial<GameEventItemMeta | EventBasePeriodScore>[];
}

export interface GameResultPeriodScore {
	period: number;
	value: number;
	tiebreakValue?: number;
}

export interface GameResultUpdatePayload {
	// Omitted when the score inputs are empty — the backend treats a missing
	// score as "no change".
	homeScore?: number;
	awayScore?: number;
	homePeriodScores: GameResultPeriodScore[];
	awayPeriodScores: GameResultPeriodScore[];
}

interface UserTimeRequest {
	id?: number;
	userId: number;
	position: string;
	secondsPlayed: number;
}

export interface TimePlayedRequest {
	userTimes: UserTimeRequest[];
}

export interface UserPlayTime {
	id: number;
	position: string;
	positionLabelKey: string;
	positionLabel: string;
	secondsPlayed: number;
}

export interface UserTime {
	userId: number;
	name: string;
	profileImageUrl: string;
	totalSeconds: number;
	playTimes: UserPlayTime[];
}

export interface Position {
	positionLabel: string;
	position: string;
}

export interface TimePlayedResponse {
	userTimes: UserTime[];
	positions: Position[];
}

export interface JerseyNumberChange {
	userId: number;
	jerseyNumber: number | null;
}
